import React from 'react';
import { useIntl } from 'react-intl';
import {
  getSchedulerTimeWindowString,
  InterPoint,
  localize,
} from '@saviynt/common';
import { Box, Tooltip, Typography } from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { Session } from '../../../models/PamModels';
import { msgs, STATUS } from '../constants';

import './SchedulerBlock.css';

function SchedulerBlock({
  details,
  startTime,
  endTime,
  timeFormat,
  isModalView,
  dataTestId,
  className,
  status,
  showRequestId,
  hasSelectedRoles,
}) {
  const classes = classnames(
    'SchedulerBlock',
    isModalView && 'SchedulerBlock-modal',
    className
  );

  const isUnavailable = status === STATUS.UNAVAILABLE;
  const isPendingSession = details && !details.sessionStatus;
  const isFutureApprovedSession = details && details.sessionStatus === 1;
  const isActiveSession = details && details.sessionStatus === 2;
  const isCompletedSession = details && details.sessionStatus === 3;

  const intl = useIntl();

  const BOOKED_LABEL = localize(intl, msgs.pam.scheduler.booked);
  const REQUESTED_LABEL = localize(intl, msgs.pam.scheduler.requested);
  const COMPLETE_LABEL = localize(intl, msgs.pam.scheduler.complete);

  const getBlockStatus = () => {
    if (isUnavailable) {
      if (isPendingSession || isActiveSession || isFutureApprovedSession) {
        return STATUS.BOOKED;
      }

      if (isCompletedSession) {
        return STATUS.COMPLETE;
      }
    }

    return STATUS.AVAILABLE;
  };

  const getBlockStatusLabel = () => {
    if (isUnavailable) {
      if (isPendingSession) {
        return REQUESTED_LABEL;
      }

      if (isActiveSession || isFutureApprovedSession) {
        return BOOKED_LABEL;
      }

      if (isCompletedSession) {
        return COMPLETE_LABEL;
      }
    }

    return null;
  };

  const setInterpointColor = () => {
    if (getBlockStatus() === STATUS.BOOKED) {
      return 'criticalBold';
    }

    if (getBlockStatus() === STATUS.COMPLETE) {
      return 'neutralSubtle';
    }

    return null;
  };

  const getTruncatedString = (str, limit) => {
    if (str.length <= limit) {
      return str;
    }

    return `${str.slice(0, limit)}...`;
  };

  const getSessionDetails = (sessionDetails) => {
    // Extract common elements
    const blockStatusClass = `SchedulerBlock-${getBlockStatus()}-details`;
    const interpointElement = (
      <div>
        <InterPoint color={setInterpointColor()} font='body2' />
      </div>
    );

    // Handle bulk select mode case
    if (hasSelectedRoles) {
      return (
        <Box className='SchedulerBlock-detailsContainer'>
          <span className='SchedulerBlock-unavailable-label'>
            {getBlockStatusLabel()}
          </span>
          {interpointElement}
          <Typography kind='body2' className={blockStatusClass}>
            {sessionDetails.roleName}
          </Typography>
        </Box>
      );
    }

    // Handle regular user details case
    const { firstname = '', lastname = '', username = '' } = sessionDetails;
    const fullName = `${firstname} ${lastname}`;
    const userText = `${fullName} (${username})`;
    const showTooltip = userText.length > 70;

    const tooltipTrigger = (
      <p className='SchedulerBlock-tooltip-container'>
        <span className='SchedulerBlock-unavailable-label'>
          {getBlockStatusLabel()}
        </span>
        <span className='InterPoint-bold'>•</span>
        <span className={blockStatusClass}>
          {getTruncatedString(`by ${userText}`, 70)}
        </span>
      </p>
    );

    return (
      <Box className='SchedulerBlock-detailsContainer'>
        <Tooltip
          text={userText}
          isDisabled={!showTooltip}
          placement='top'
          trigger={tooltipTrigger}
        />
        {showRequestId && (
          <>
            {interpointElement}
            <Typography kind='body2' className={blockStatusClass}>
              {`#${sessionDetails.requestId}`}
            </Typography>
          </>
        )}
      </Box>
    );
  };

  return (
    <section className={classes} data-testid={dataTestId}>
      <section className={`SchedulerBlock-${getBlockStatus()}`}>
        <Box tag='div' className={`SchedulerBlock-${getBlockStatus()}-content`}>
          <Box tag='div' className='SchedulerBlock-timeRange'>
            <Typography
              kind='body2'
              className={`SchedulerBlock-${getBlockStatus()}-timeRange`}>
              {`${getSchedulerTimeWindowString(
                startTime,
                timeFormat.roundToNearest,
                timeFormat.is24HourFormat
              )} - ${getSchedulerTimeWindowString(
                endTime,
                timeFormat.roundToNearest,
                timeFormat.is24HourFormat
              )}`}
            </Typography>
          </Box>
          <Typography
            kind={
              getBlockStatus() === STATUS.AVAILABLE ? 'body2-bold' : 'body2'
            }
            className={`SchedulerBlock-${getBlockStatus()}-details`}>
            {details && isUnavailable
              ? getSessionDetails(details)
              : localize(intl, msgs.pam.scheduler.availableForRequest)}
          </Typography>
        </Box>
      </section>
    </section>
  );
}

SchedulerBlock.propTypes = {
  details: Session,
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string.isRequired,
  timeFormat: PropTypes.shape({
    roundToNearest: PropTypes.string,
    is24HourFormat: PropTypes.bool,
  }).isRequired,
  isModalView: PropTypes.bool,
  dataTestId: PropTypes.string,
  className: PropTypes.string,
  status: PropTypes.string,
  showRequestId: PropTypes.bool,
  hasSelectedRoles: PropTypes.bool,
};

SchedulerBlock.defaultProps = {
  details: null,
  isModalView: false,
  dataTestId: null,
  className: null,
  status: null,
  showRequestId: true,
  hasSelectedRoles: false,
};

export default SchedulerBlock;
